import type { Island } from '../types'
import { freshSrs } from '../lib/srs'
import { MORE_ISLANDS } from './islandsMore'
import { MORE_LATIN } from './islandsMoreLatin'

/**
 * Starter islands for the Latin-script languages, roughly A2–B1.
 *
 * The same four topics in every language — work, why you study it, family, a
 * restaurant — so switching languages feels like the same app rather than four
 * different ones, and so a new language is a block of data rather than a code
 * change.
 *
 * Same principle as the Mandarin set: concrete enough to be worth saying, and
 * wrong about you in the details on purpose, so the editor gets used before
 * anything is memorised. A fluent sentence about a life you do not have is
 * worse than silence in a real conversation.
 *
 * There is no phonetic line — these spellings are regular enough that a second
 * transcription would be noise. `pinyin` stays empty; lib/languages.ts explains
 * why the field is still called that.
 */

export type SeedBlock = [text: string, english: string, note?: string]

export interface SeedIsland {
  slug: string
  title: string
  /** The island's name in its own language. */
  nativeTitle: string
  useWhen: string
  tags: string[]
  level: number
  blocks: SeedBlock[]
}

const SEED_CLOCK = 0

const CONTENT: Record<string, SeedIsland[]> = {
  de: [
    {
      slug: 'arbeit',
      title: 'My work',
      nativeTitle: 'Meine Arbeit',
      useWhen: 'The second question anyone asks. Have all of it ready cold.',
      tags: ['introductions', 'work'],
      level: 2,
      blocks: [
        ['Ich bin Softwareentwickler.', "I'm a software developer."],
        [
          'Ich arbeite seit fünf Jahren bei einer Technologiefirma.',
          "I've worked at a technology company for five years.",
          'seit + dative with the present tense — German says "I work since", not "I have worked".',
        ],
        ['Ich bin hauptsächlich für die Backend-Entwicklung zuständig.', "I'm mainly responsible for back-end development."],
        [
          'Normalerweise arbeite ich von zu Hause aus.',
          'I usually work from home.',
          'The verb stays second: Normalerweise *arbeite* ich, never Normalerweise ich arbeite.',
        ],
        [
          'Der Job ist ziemlich stressig, aber er gefällt mir sehr.',
          'The job is fairly stressful, but I like it a lot.',
          'gefallen puts the person in the dative: *mir* gefällt es.',
        ],
      ],
    },
    {
      slug: 'warum',
      title: 'Why I study German',
      nativeTitle: 'Warum ich Deutsch lerne',
      useWhen: 'Fires the moment anyone compliments your German. It always comes.',
      tags: ['introductions', 'language'],
      level: 2,
      blocks: [
        ['Ich lerne seit etwa zwei Jahren Deutsch.', "I've been learning German for about two years."],
        [
          'Am Anfang war es nur für die Arbeit, aber dann hat es mir wirklich Spaß gemacht.',
          'At first it was only for work, but then I really came to enjoy it.',
          'Spaß machen takes the dative — es macht *mir* Spaß.',
        ],
        ['Am schwierigsten finde ich die Fälle.', 'I find the cases hardest.'],
        ['Ich übe jeden Morgen eine halbe Stunde.', 'I practise for half an hour every morning.'],
        [
          'Mein Ziel ist es, mich mit Freunden ganz normal unterhalten zu können.',
          'My goal is to be able to chat with friends completely normally.',
          'sich unterhalten is reflexive, and the infinitive clause sends können to the end.',
        ],
      ],
    },
    {
      slug: 'familie',
      title: 'My family',
      nativeTitle: 'Meine Familie',
      useWhen: 'Rewrite this one first. Wrong details here are worse than none.',
      tags: ['introductions', 'family'],
      level: 2,
      blocks: [
        ['Wir sind vier in der Familie.', 'There are four of us in the family.'],
        ['Meine Frau, unsere zwei Kinder und ich.', 'My wife, our two children, and me.'],
        [
          'Meine Frau ist Lehrerin und unterrichtet Englisch.',
          'My wife is a teacher and teaches English.',
          'No article with professions: sie ist Lehrerin, not eine Lehrerin.',
        ],
        ['Die Ältere geht schon zur Schule, die Jüngere noch in den Kindergarten.', 'The older one is already at school, the younger still at kindergarten.'],
        [
          'Meine Eltern wohnen nicht weit weg und kommen oft am Wochenende vorbei.',
          'My parents live nearby and often come over at the weekend.',
          'vorbeikommen is separable — the prefix goes to the very end.',
        ],
      ],
    },
    {
      slug: 'restaurant',
      title: 'Ordering a meal',
      nativeTitle: 'Im Restaurant',
      useWhen: 'Transactional, frequent, unforgiving of hesitation. Drill to reflex.',
      tags: ['transactional', 'food'],
      level: 1,
      blocks: [
        ['Guten Abend, ein Tisch für zwei Personen, bitte.', 'Good evening, a table for two, please.'],
        ['Könnten wir bitte die Karte haben?', 'Could we have the menu, please?', 'Karte, not Menü — a Menü is a set meal.'],
        ['Ich nehme das Schnitzel mit Kartoffelsalat.', "I'll have the schnitzel with potato salad."],
        [
          'Könnten Sie das bitte ohne Zwiebeln machen?',
          'Could you make that without onions, please?',
          'Sie stays capitalised — sie would be "they".',
        ],
        ['Wir möchten bitte zahlen.', "We'd like to pay, please."],
      ],
    },
  ],

  fr: [
    {
      slug: 'travail',
      title: 'My work',
      nativeTitle: 'Mon travail',
      useWhen: 'The second question anyone asks. Have all of it ready cold.',
      tags: ['introductions', 'work'],
      level: 2,
      blocks: [
        ['Je suis développeur de logiciels.', "I'm a software developer."],
        [
          "Je travaille depuis cinq ans dans une entreprise de technologie.",
          "I've worked at a technology company for five years.",
          'depuis + present tense, exactly like German seit — not the perfect.',
        ],
        ["Je m'occupe principalement du développement back-end.", "I'm mainly responsible for back-end development."],
        ["D'habitude, je travaille depuis chez moi.", 'I usually work from home.'],
        [
          'Ce travail est assez stressant, mais il me plaît beaucoup.',
          'This job is fairly stressful, but I like it a lot.',
          'plaire puts the person in the indirect object: il *me* plaît.',
        ],
      ],
    },
    {
      slug: 'pourquoi',
      title: 'Why I study French',
      nativeTitle: "Pourquoi j'apprends le français",
      useWhen: 'Fires the moment anyone compliments your French. It always comes.',
      tags: ['introductions', 'language'],
      level: 2,
      blocks: [
        ["J'apprends le français depuis environ deux ans.", "I've been learning French for about two years."],
        ["Au début, c'était seulement pour le travail, mais ensuite ça m'a vraiment plu.", 'At first it was only for work, but then I really came to enjoy it.'],
        ["Ce que je trouve le plus difficile, c'est la prononciation.", 'What I find hardest is the pronunciation.'],
        ["Je m'entraîne une demi-heure tous les matins.", 'I practise for half an hour every morning.'],
        [
          "Mon objectif, c'est de pouvoir discuter naturellement avec des amis.",
          'My goal is to be able to chat naturally with friends.',
          "Mon objectif, c'est de… — the c'est is doing real work; dropping it sounds written, not spoken.",
        ],
      ],
    },
    {
      slug: 'famille',
      title: 'My family',
      nativeTitle: 'Ma famille',
      useWhen: 'Rewrite this one first. Wrong details here are worse than none.',
      tags: ['introductions', 'family'],
      level: 2,
      blocks: [
        ['Nous sommes quatre dans la famille.', 'There are four of us in the family.'],
        ['Ma femme, nos deux enfants et moi.', 'My wife, our two children, and me.'],
        ["Ma femme est professeure et enseigne l'anglais.", 'My wife is a teacher and teaches English.'],
        ["L'aînée va déjà à l'école, la plus jeune est encore à la maternelle.", 'The older one already goes to school, the younger is still at nursery.'],
        ['Mes parents habitent tout près et viennent souvent le week-end.', 'My parents live very close and often come at the weekend.'],
      ],
    },
    {
      slug: 'restaurant',
      title: 'Ordering a meal',
      nativeTitle: 'Au restaurant',
      useWhen: 'Transactional, frequent, unforgiving of hesitation. Drill to reflex.',
      tags: ['transactional', 'food'],
      level: 1,
      blocks: [
        ['Bonsoir, une table pour deux, s’il vous plaît.', 'Good evening, a table for two, please.'],
        ['Est-ce qu’on pourrait avoir la carte, s’il vous plaît ?', 'Could we have the menu, please?', 'la carte is the menu; le menu is a set meal.'],
        ['Je vais prendre le poulet avec des frites.', "I'll have the chicken with chips."],
        ['Sans oignons, s’il vous plaît.', 'Without onions, please.'],
        ['L’addition, s’il vous plaît.', 'The bill, please.'],
      ],
    },
  ],

  es: [
    {
      slug: 'trabajo',
      title: 'My work',
      nativeTitle: 'Mi trabajo',
      useWhen: 'The second question anyone asks. Have all of it ready cold.',
      tags: ['introductions', 'work'],
      level: 2,
      blocks: [
        ['Soy desarrollador de software.', "I'm a software developer."],
        [
          'Llevo cinco años trabajando en una empresa de tecnología.',
          "I've been working at a technology company for five years.",
          'llevar + time + gerund is how Spanish says this. "He trabajado por cinco años" is a translation, not Spanish.',
        ],
        ['Me encargo principalmente del desarrollo backend.', "I'm mainly responsible for back-end development."],
        ['Normalmente trabajo desde casa.', 'I usually work from home.'],
        [
          'Es un trabajo bastante estresante, pero me gusta mucho.',
          "It's a fairly stressful job, but I like it a lot.",
          'gustar puts the person in the indirect object: *me* gusta.',
        ],
      ],
    },
    {
      slug: 'porque',
      title: 'Why I study Spanish',
      nativeTitle: 'Por qué estudio español',
      useWhen: 'Fires the moment anyone compliments your Spanish. It always comes.',
      tags: ['introductions', 'language'],
      level: 2,
      blocks: [
        ['Llevo unos dos años estudiando español.', "I've been studying Spanish for about two years."],
        ['Al principio era solo por el trabajo, pero luego me gustó de verdad.', 'At first it was only for work, but then I really came to like it.'],
        ['Lo que me parece más difícil es el subjuntivo.', 'What I find hardest is the subjunctive.'],
        ['Practico media hora todas las mañanas.', 'I practise for half an hour every morning.'],
        ['Mi objetivo es poder charlar con amigos con naturalidad.', 'My goal is to be able to chat with friends naturally.'],
      ],
    },
    {
      slug: 'familia',
      title: 'My family',
      nativeTitle: 'Mi familia',
      useWhen: 'Rewrite this one first. Wrong details here are worse than none.',
      tags: ['introductions', 'family'],
      level: 2,
      blocks: [
        ['Somos cuatro en la familia.', 'There are four of us in the family.'],
        ['Mi mujer, nuestros dos hijos y yo.', 'My wife, our two children, and me.'],
        ['Mi mujer es profesora y enseña inglés.', 'My wife is a teacher and teaches English.'],
        ['La mayor ya va al colegio, la pequeña todavía va a la guardería.', 'The older one already goes to school, the younger still goes to nursery.'],
        ['Mis padres viven cerca y vienen a menudo los fines de semana.', 'My parents live nearby and often come at weekends.'],
      ],
    },
    {
      slug: 'restaurante',
      title: 'Ordering a meal',
      nativeTitle: 'En el restaurante',
      useWhen: 'Transactional, frequent, unforgiving of hesitation. Drill to reflex.',
      tags: ['transactional', 'food'],
      level: 1,
      blocks: [
        ['Buenas noches, una mesa para dos, por favor.', 'Good evening, a table for two, please.'],
        ['¿Nos trae la carta, por favor?', 'Could you bring us the menu, please?'],
        ['Voy a tomar el pollo con patatas.', "I'll have the chicken with potatoes."],
        ['Sin cebolla, por favor.', 'Without onion, please.'],
        ['La cuenta, por favor.', 'The bill, please.'],
      ],
    },
  ],

  it: [
    {
      slug: 'lavoro',
      title: 'My work',
      nativeTitle: 'Il mio lavoro',
      useWhen: 'The second question anyone asks. Have all of it ready cold.',
      tags: ['introductions', 'work'],
      level: 2,
      blocks: [
        ['Faccio lo sviluppatore di software.', "I'm a software developer.", 'fare il/lo + job is the everyday way to say what you do.'],
        ["Lavoro da cinque anni in un'azienda tecnologica.", "I've worked at a technology company for five years.", 'da + present tense, like French depuis.'],
        ['Mi occupo soprattutto dello sviluppo back-end.', "I'm mainly responsible for back-end development."],
        ['Di solito lavoro da casa.', 'I usually work from home.'],
        ['È un lavoro abbastanza stressante, ma mi piace molto.', "It's a fairly stressful job, but I like it a lot."],
      ],
    },
    {
      slug: 'perche',
      title: 'Why I study Italian',
      nativeTitle: "Perché studio l'italiano",
      useWhen: 'Fires the moment anyone compliments your Italian. It always comes.',
      tags: ['introductions', 'language'],
      level: 2,
      blocks: [
        ['Studio italiano da circa due anni.', "I've been studying Italian for about two years."],
        ["All'inizio era solo per lavoro, poi mi è piaciuto davvero.", 'At first it was only for work, then I really came to like it.'],
        ['La cosa più difficile per me è il congiuntivo.', 'The hardest thing for me is the subjunctive.'],
        ['Mi esercito mezz’ora ogni mattina.', 'I practise for half an hour every morning.'],
        ['Il mio obiettivo è riuscire a chiacchierare con gli amici in modo naturale.', 'My goal is to manage to chat with friends naturally.'],
      ],
    },
    {
      slug: 'famiglia',
      title: 'My family',
      nativeTitle: 'La mia famiglia',
      useWhen: 'Rewrite this one first. Wrong details here are worse than none.',
      tags: ['introductions', 'family'],
      level: 2,
      blocks: [
        ['Siamo in quattro in famiglia.', 'There are four of us in the family.'],
        ['Mia moglie, i nostri due figli e io.', 'My wife, our two children, and me.'],
        [
          "Mia moglie fa l'insegnante e insegna inglese.",
          'My wife is a teacher and teaches English.',
          'Close family in the singular drops the article: *mia* moglie, not la mia moglie.',
        ],
        ['La più grande va già a scuola, la più piccola è ancora all’asilo.', 'The older one already goes to school, the younger is still at nursery.'],
        ['I miei genitori abitano vicino e vengono spesso nel fine settimana.', 'My parents live nearby and often come at the weekend.'],
      ],
    },
    {
      slug: 'ristorante',
      title: 'Ordering a meal',
      nativeTitle: 'Al ristorante',
      useWhen: 'Transactional, frequent, unforgiving of hesitation. Drill to reflex.',
      tags: ['transactional', 'food'],
      level: 1,
      blocks: [
        ['Buonasera, un tavolo per due, per favore.', 'Good evening, a table for two, please.'],
        ['Potremmo avere il menù, per favore?', 'Could we have the menu, please?'],
        ['Prendo il pollo con le patate.', "I'll have the chicken with potatoes."],
        ['Senza cipolla, per favore.', 'Without onion, please.'],
        ['Il conto, per favore.', 'The bill, please.'],
      ],
    },
  ],
}

/** Islands for one Latin-script language. Empty for anything not listed above. */
export function buildLatinIslands(language: string, now = Date.now()): Island[] {
  const topics = [
    ...(CONTENT[language] ?? []),
    ...(MORE_ISLANDS[language] ?? []),
    ...(MORE_LATIN[language] ?? []),
  ]
  return topics.map((seed) => ({
    id: `${language}_${seed.slug}`,
    language,
    title: seed.title,
    // The Chinese-named field carries whatever the language's own title is.
    titleZh: seed.nativeTitle,
    useWhen: seed.useWhen,
    tags: seed.tags,
    hskLevel: seed.level,
    blocks: seed.blocks.map(([text, english, note], i) => ({
      id: `${language}_${seed.slug}_b${i + 1}`,
      hanzi: text,
      pinyin: '',
      english,
      ...(note ? { note } : {}),
    })),
    srs: freshSrs(),
    stats: { performances: 0, bestScore: 0, lastScore: null, lastDrilledAt: null },
    isStarter: true,
    createdAt: now,
    updatedAt: SEED_CLOCK,
  }))
}

export const SEEDED_LATIN_LANGUAGES = Object.keys(CONTENT)
